/**
 * The notes of a watched video, `video-notes/<name>.md`, which the agent writes and the bar under the
 * video panel reads. Plain .ts so `node --test` can load it.
 */
const HEADING = /^## \[(?:(\d{1,2}):)?(\d{1,2}):(\d{2})\]\s*(★?)\s*(.*)$/;

export interface VideoNote {
	seconds: number;
	/** The user asked for this one. */
	starred: boolean;
	title: string;
	body: string;
}

/** A heading that cannot be read is skipped with its body, so one bad note never hides the rest. */
export function parseVideoNotes(markdown: string): VideoNote[] {
	const notes: VideoNote[] = [];
	let current: { note: VideoNote; lines: string[] } | null = null;
	const close = () => {
		if (current) notes.push({ ...current.note, body: current.lines.join("\n").trim() });
		current = null;
	};
	for (const line of markdown.split(/\r?\n/)) {
		if (!line.startsWith("## ")) {
			current?.lines.push(line);
			continue;
		}
		close();
		const match = HEADING.exec(line);
		if (!match) continue;
		const [hours, minutes, secs] = [Number(match[1] ?? 0), Number(match[2]), Number(match[3])];
		if (minutes > 59 || secs > 59) continue;
		current = { note: { seconds: hours * 3600 + minutes * 60 + secs, starred: match[4] === "★", title: match[5]!.trim(), body: "" }, lines: [] };
	}
	close();
	return notes;
}

/** The index of the last note the video has reached, or -1 before the first. */
export function currentNote(notes: VideoNote[], seconds: number): number {
	let found = -1;
	notes.forEach((note, i) => {
		if (note.seconds <= seconds) found = i;
	});
	return found;
}

/** The upload route may rename a file, so the notes follow the path it returned, not the video's name. */
export function notesPathFor(audioPath: string): string {
	const base = audioPath.slice(audioPath.lastIndexOf("/") + 1);
	const dot = base.lastIndexOf(".");
	return `video-notes/${dot > 0 ? base.slice(0, dot) : base}.md`;
}
